import { Text } from '@/components/atoms/text';
import { StyledDivider } from '../styles';
import { DimensionNote } from '@/components/molecules/dimensionNote';
import { Flex } from '@/styles/globalStyles';
import { BarChart, CheckCircleOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { DimensionsNoteProps } from '@/types/dimension';
import { icon } from '@/utils/functions/tabIcons';
import { sort } from '@/utils/functions/sort';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from '@/hooks/useTranslation';
import { ClassificationOpportunityAndRisk } from '@/types/classification';
import { OpportunityAndRisk } from '@/types/opportunityAndRisk';
import { opportunityAndRisk } from '@/utils/functions/opportunityAndRisk';
import { truncateString } from '@/utils/functions/stringFormat';
import { useMediaQuery } from '@mui/material';
import { StyledContainer } from './styles';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';

export const DimensionsNote = ({ data }: DimensionsNoteProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { t } = useTranslation();

  const customOrder = [1, 2, 3, 4, 5, 6, 7, 8];

  const orderedDimensionScores = sort.sortDimensions(
    data?.dimensions?.filter((item) => customOrder.includes(item.dimension.order)) || [],
    customOrder
  );

  const validClassifications = Object.values(ClassificationOpportunityAndRisk);

  const getDisplayValue = (
    value: string | number | undefined,
    variant: 'opportunity' | 'risk'
  ): string | number => {
    if (value === undefined || value === null) return 0;
    if (typeof value === 'number') return value;
    if (value === '' || !validClassifications.includes(value as ClassificationOpportunityAndRisk)) {
      return 0;
    }
    return opportunityAndRisk.normalize(value as OpportunityAndRisk, variant);
  };

  return (
    <StyledContainer gap={2} variant="outlined">
      <Text variant="h6" color="text.primary">
        {t('page.municipality.municipality.card.scores.title')}
      </Text>

      <Text variant="body2" color="text.secondary">
        <SafeMarkdown text={t('page.municipality.municipality.card.scores.description')} />
      </Text>

      <DimensionNote
        title={{
          text: t('dimension.dimension.title'),
          variant: 'subtitle2',
          color: 'text.secondary',
        }}
        label={{
          opportunity: {
            icon: CheckCircleOutlined,
            text: truncateString(t('dimension.dimension.opportunity'), isMobile ? 5 : 20),
          },
          risk: {
            icon: WarningAmberOutlined,
            text: t('dimension.dimension.risk'),
          },
        }}
      />

      <StyledDivider />

      <Flex direction="column" gap={1.5} fullWidth>
        {orderedDimensionScores.map((item, index) => {
          if (item?.dimension.name !== 'Geral') {
            return (
              <DimensionNote
                key={index}
                icon={{
                  component: icon.getDimensionIcon(item?.dimension.icon || ''),
                  color: 'text.secondary',
                }}
                title={{
                  text: item?.dimension.name || '',
                  variant: 'body2',
                  color: 'text.secondary',
                }}
                note={{
                  opportunity: getDisplayValue(
                    item.classification_opportunity ?? item?.score_opportunity,
                    'opportunity'
                  ),
                  risk: getDisplayValue(item.classification_risk ?? item?.score_risk, 'risk'),
                }}
              />
            );
          }
        })}

        <Flex direction="column" gap={1.5} fullWidth>
          <StyledDivider />
          <DimensionNote
            icon={{
              component: BarChart,
              color: 'text.secondary',
            }}
            title={{
              text: t('page.municipality.municipality.card.scores.footer'),
              variant: 'subtitle2',
              color: 'text.primary',
            }}
            note={{
              opportunity: data?.municipality?.municipality_score?.general_score_opportunity || 0,
              risk: data?.municipality?.municipality_score?.general_score_risk || 0,
              isGeneralScore: true,

              highText:
                data?.municipality?.municipality_score?.classification_opportunity ==
                ClassificationOpportunityAndRisk.HIGH
                  ? t('label.label.high_opportunity')
                  : data?.municipality?.municipality_score?.classification_opportunity ==
                    ClassificationOpportunityAndRisk.MEDIUM_HIGH
                  ? t('label.label.medium_high_opportunity')
                  : data?.municipality?.municipality_score?.classification_opportunity ==
                    ClassificationOpportunityAndRisk.MEDIUM_LOW
                  ? t('label.label.medium_low_opportunity')
                  : t('label.label.low_opportunity'),

              lowText:
                data?.municipality?.municipality_score?.classification_risk ==
                ClassificationOpportunityAndRisk.HIGH
                  ? t('label.label.high_risk')
                  : data?.municipality?.municipality_score?.classification_risk ==
                    ClassificationOpportunityAndRisk.MEDIUM_HIGH
                  ? t('label.label.medium_high_risk')
                  : data?.municipality?.municipality_score?.classification_risk ==
                    ClassificationOpportunityAndRisk.MEDIUM_LOW
                  ? t('label.label.medium_low_risk')
                  : t('label.label.low_risk'),
            }}
          />
        </Flex>
      </Flex>
    </StyledContainer>
  );
};
